import React, { Component } from "react";
import Breadcrumb from "../common/breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  RotateCcw,
  Clock,
  CheckCircle,
  XCircle,
  Check,
  X,
  Search,
  Calendar,
  MessageCircle,
} from "react-feather";
import EmptyState from "../common/EmptyState";
import "../1688-taobao-orders/orders-kido.css";
import "./refunds-kido.css";
import {
  getRefundRequests,
  getAllPendingRefundRequests,
  updateRefundStatus,
  isOrderPaidWithBkash,
  refundWithBkash,
  searchBkashTransaction,
} from "../../firebase/firebase.utils";

const TABS = ["pending", "approved", "rejected"];

// Each status gets a playroom tone (.kd-st--* in orders-kido.css) and words
// for its sticker tab and empty list.
const TAB_INFO = {
  pending:  { label: "Waiting",  tone: "butter", Icon: Clock,       empty: "No refunds waiting. All caught up!" },
  approved: { label: "Approved", tone: "mint",   Icon: CheckCircle, empty: "No approved refunds yet." },
  rejected: { label: "Rejected", tone: "rose",   Icon: XCircle,     empty: "No rejected refunds yet." },
};

const STATUS_TONE = { pending: "butter", approved: "mint", rejected: "rose" };

const MODAL_CONFIG = {
  approve: {
    title: "Approve refund",
    confirmLabel: "Yes, approve",
    tone: "mint",
    Icon: Check,
    description: "This refund request will be marked as approved.",
  },
  reject: {
    title: "Reject refund",
    confirmLabel: "Yes, reject",
    tone: "rose",
    Icon: X,
    description: "This refund request will be marked as rejected.",
  },
};

class RefundRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "pending",
      pending:  { docs: [], loading: true,  lastCreatedAt: null, hasMore: false },
      // `loaded` tells "not fetched yet" apart from "fetched and empty".
      approved: { docs: [], loading: false, loaded: false, lastCreatedAt: null, hasMore: false },
      rejected: { docs: [], loading: false, loaded: false, lastCreatedAt: null, hasMore: false },
      loadingMore: false,
      updatingId: null,
      // modal
      modalOpen: false,
      modalAction: null,
      modalDoc: null,
      adminNote: "",
      refundedAmount: "",
      // null = still checking, true/false once known
      modalIsBkash: null,
      // bKash TrxID lookup
      trxQuery: "",
      trxLoading: false,
      trxResult: null,
      trxError: "",
    };
  }

  async componentDidMount() {
    await this.loadPending();
  }

  loadPending = async () => {
    this.setState((s) => ({ pending: { ...s.pending, loading: true } }));
    const docs = await getAllPendingRefundRequests();
    this.setState({ pending: { docs, loading: false, lastCreatedAt: null, hasMore: false } });
  };

  loadTab = async (tab, reset = false) => {
    if (tab === "pending") { await this.loadPending(); return; }
    const current = this.state[tab];
    const cursor = reset ? null : current.lastCreatedAt;
    if (reset) {
      this.setState((s) => ({ [tab]: { ...s[tab], loading: true, docs: [] } }));
    } else {
      this.setState({ loadingMore: true });
    }
    const result = await getRefundRequests(tab, cursor);
    this.setState((s) => ({
      loadingMore: false,
      [tab]: {
        loading: false,
        loaded: true,
        docs: reset ? result.docs : [...s[tab].docs, ...result.docs],
        lastCreatedAt: result.lastCreatedAt,
        hasMore: result.hasMore,
      },
    }));
  };

  switchTab = async (tab) => {
    if (tab === this.state.activeTab) return;
    this.setState({ activeTab: tab });
    const tabState = this.state[tab];
    if (tabState.docs.length === 0 && !tabState.loading) {
      await this.loadTab(tab, true);
    }
  };

  openModal = (action, doc) => {
    this.setState({
      modalOpen: true, modalAction: action, modalDoc: doc,
      adminNote: "", refundedAmount: "", modalIsBkash: action === "approve" ? null : false,
    });
    if (action === "approve") {
      // bKash-paid orders are refunded through bKash on approval, so the modal
      // has to know before the admin confirms.
      isOrderPaidWithBkash(doc.orderId)
        .then((isBkash) => {
          if (this.state.modalDoc?.id === doc.id) this.setState({ modalIsBkash: isBkash });
        })
        .catch(() => {
          if (this.state.modalDoc?.id === doc.id) this.setState({ modalIsBkash: false });
        });
    }
  };

  closeModal = () => {
    if (this.state.updatingId) return;
    this.setState({ modalOpen: false, modalAction: null, modalDoc: null, adminNote: "", refundedAmount: "" });
  };

  confirmAction = async () => {
    const { modalAction, modalDoc, adminNote, refundedAmount, modalIsBkash } = this.state;
    if (!modalDoc) return;
    if (modalAction === "approve") {
      const amt = parseFloat(refundedAmount);
      if (!refundedAmount || isNaN(amt) || amt <= 0) {
        toast.error("Please enter the refunded amount.");
        return;
      }
      if (modalIsBkash === null) {
        toast.error("Still checking how this order was paid — try again in a moment.");
        return;
      }
    }
    this.setState({ updatingId: modalDoc.id });
    try {
      const amt = modalAction === "approve" ? parseFloat(refundedAmount) : null;
      let note = adminNote.trim();
      let extra = {};

      // Money first, then the record: if bKash refuses or doesn't answer, the
      // request stays pending rather than claiming a refund that didn't happen.
      if (modalAction === "approve" && modalIsBkash) {
        const result = await refundWithBkash({
          orderId: modalDoc.orderId,
          amount: amt,
          reason: modalDoc.refundNote || "Customer refund",
          refundRequestId: modalDoc.id,
        });
        if (!result.ok) {
          toast.error(result.message);
          this.setState({ updatingId: null });
          return;
        }
        note = [note, `bKash refund TrxID: ${result.refundTrxId}`].filter(Boolean).join(" | ");
        extra = { refundMethod: "bKash", bkashRefundTrxId: result.refundTrxId };
        toast.success(result.message);
      }

      await updateRefundStatus(modalDoc.id, modalAction, note, amt, modalDoc.orderId || null, extra);
      toast.success(modalAction === "approve" ? "Refund approved." : "Refund rejected.");
      this.setState({ modalOpen: false, modalAction: null, modalDoc: null, adminNote: "" });
      await this.loadPending();
      const otherTab = modalAction === "approve" ? "approved" : "rejected";
      this.setState((s) => ({
        [otherTab]: { ...s[otherTab], docs: [], loaded: false, lastCreatedAt: null, hasMore: false },
      }));
    } catch (e) {
      toast.error("Failed: " + e.message);
    }
    this.setState({ updatingId: null });
  };

  formatDate = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-BD", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  renderModal() {
    const { modalOpen, modalAction, modalDoc, adminNote, refundedAmount, updatingId, modalIsBkash } = this.state;
    if (!modalOpen || !modalDoc) return null;
    const cfg = MODAL_CONFIG[modalAction];
    const isRejection = modalAction === "reject";
    const isLoading = updatingId === modalDoc.id;
    const statusTone = STATUS_TONE[modalDoc.refundStatus] || "ink";

    return (
      <div className="kd-rf-overlay" onClick={this.closeModal}>
        <div
          className={`kd-rf-modal kd-st--${cfg.tone}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kd-rf-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="kd-rf-modal-hole" aria-hidden="true" />
          <button
            type="button"
            className="kd-rf-modal-close"
            onClick={this.closeModal}
            disabled={isLoading}
            aria-label="Close"
          >
            <X size={16} />
          </button>

          <div className="kd-rf-modal-head">
            <span className="kd-rf-modal-icon" aria-hidden="true">
              <cfg.Icon size={22} />
            </span>
            <h3 id="kd-rf-modal-title">{cfg.title}</h3>
            <p>{cfg.description}</p>
          </div>

          <div className="kd-rf-details">
            <div>
              <span>Order</span>
              <strong className="kd-rf-order">#{modalDoc.orderId || modalDoc.id}</strong>
            </div>
            <div>
              <span>Amount paid</span>
              <strong>{modalDoc.orderTotalPaid != null ? `৳ ${modalDoc.orderTotalPaid}` : "—"}</strong>
            </div>
            <div>
              <span>Requested</span>
              <strong className="kd-rf-small">{this.formatDate(modalDoc.createdAt)}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>
                <span className={`kd-status-badge kd-st--${statusTone}`}>
                  <span className="kd-status-dot" />
                  {modalDoc.refundStatus}
                </span>
              </strong>
            </div>
          </div>

          {modalDoc.refundNote && (
            <div className="kd-rf-reason">
              <span>Customer's reason</span>
              <p>{modalDoc.refundNote}</p>
            </div>
          )}

          {!isRejection && (
            <label className="kd-rf-field">
              <span>
                Refund amount <em aria-hidden="true">*</em>
                {modalDoc.orderTotalPaid != null && (
                  <small>Paid ৳{modalDoc.orderTotalPaid}</small>
                )}
              </span>
              <span className="kd-rf-money-input">
                <b aria-hidden="true">৳</b>
                <input
                  type="number"
                  min={0}
                  max={modalDoc.orderTotalPaid || undefined}
                  placeholder="Amount to send back"
                  value={refundedAmount}
                  onChange={(e) => this.setState({ refundedAmount: e.target.value })}
                  disabled={isLoading}
                  autoFocus
                />
              </span>
            </label>
          )}

          {!isRejection && modalIsBkash === null && (
            <p className="kd-rf-checking">
              <span className="kd-hop" aria-hidden="true"><i /><i /><i /></span>
              Checking how this order was paid…
            </p>
          )}
          {!isRejection && modalIsBkash && (
            <div className="kd-rf-bkash-warn" role="alert">
              <strong>Paid with bKash.</strong> Approving will immediately send
              {refundedAmount ? ` ৳${refundedAmount}` : " the amount above"} back to
              the customer&apos;s bKash account. This can&apos;t be undone.
            </div>
          )}

          {isRejection && (
            <label className="kd-rf-field">
              <span>
                Note for the record <small>Optional</small>
              </span>
              <textarea
                rows={3}
                placeholder="Why is this refund rejected?"
                value={adminNote}
                onChange={(e) => this.setState({ adminNote: e.target.value })}
                disabled={isLoading}
                autoFocus
              />
            </label>
          )}

          <div className="kd-rf-modal-actions">
            <button type="button" className="kd-rf-btn kd-rf-btn--ghost" onClick={this.closeModal} disabled={isLoading}>
              Cancel
            </button>
            <button
              type="button"
              className={`kd-rf-btn kd-rf-btn--${cfg.tone}`}
              onClick={this.confirmAction}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="kd-hop" aria-hidden="true"><i /><i /><i /></span>
                  Working on it
                </>
              ) : (
                cfg.confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderList(tab) {
    const { docs, loading, hasMore } = this.state[tab];
    const { loadingMore, updatingId } = this.state;
    const isPending = tab === "pending";

    if (loading) {
      return (
        <div className="kd-rf-list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="kd-rf-ticket is-skeleton" aria-hidden="true">
              <span className="kd-skel kd-skel--sm" />
              <span className="kd-skel" />
              <span className="kd-skel" />
            </div>
          ))}
        </div>
      );
    }

    if (docs.length === 0) {
      return <EmptyState>{TAB_INFO[tab].empty}</EmptyState>;
    }

    return (
      <>
        <ol className="kd-rf-list">
          {docs.map((doc, i) => {
            const tone = STATUS_TONE[doc.refundStatus] || "butter";
            const isBusy = updatingId === doc.id;
            return (
              <li
                key={doc.id}
                className={`kd-rf-ticket kd-st--${tone}`}
                style={{ "--i": Math.min(i, 10) }}
              >
                <div className="kd-rf-ticket-main">
                  <div className="kd-rf-ticket-top">
                    <span className="kd-rf-num">{i + 1}</span>
                    <span className="kd-id-tag">#{doc.orderId || doc.id}</span>
                    <span className={`kd-status-badge kd-st--${tone}`}>
                      <span className="kd-status-dot" />
                      {doc.refundStatus}
                    </span>
                  </div>

                  {doc.refundNote ? (
                    <p className="kd-rf-bubble">
                      <MessageCircle size={13} aria-hidden="true" />
                      {doc.refundNote}
                    </p>
                  ) : (
                    <p className="kd-rf-bubble is-empty">No reason given.</p>
                  )}

                  <div className="kd-rf-meta">
                    <span>
                      <Calendar size={12} aria-hidden="true" />
                      {this.formatDate(doc.createdAt)}
                    </span>
                    <span title={doc.id}>Request {doc.id?.slice(0, 8)}…</span>
                  </div>

                  {!isPending && (
                    <p className="kd-rf-admin-note">
                      <strong>Admin note</strong>
                      {doc.adminNote || "—"}
                    </p>
                  )}
                </div>

                <div className="kd-rf-ticket-stub">
                  <span className="kd-rf-stub-label">Paid</span>
                  <span className="kd-price-sticker">
                    {doc.orderTotalPaid != null ? `৳ ${doc.orderTotalPaid}` : "—"}
                  </span>
                  {isPending &&
                    (isBusy ? (
                      <span className="kd-hop" aria-label="Updating"><i /><i /><i /></span>
                    ) : (
                      <div className="kd-rf-stub-actions">
                        <button
                          type="button"
                          className="kd-act kd-act--mint"
                          onClick={() => this.openModal("approve", doc)}
                        >
                          <Check size={13} aria-hidden="true" />
                          Approve
                        </button>
                        <button
                          type="button"
                          className="kd-act kd-act--rose"
                          onClick={() => this.openModal("reject", doc)}
                        >
                          <X size={13} aria-hidden="true" />
                          Reject
                        </button>
                      </div>
                    ))}
                </div>
              </li>
            );
          })}
        </ol>

        {hasMore && (
          <div className="kd-rf-more">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => this.loadTab(tab)}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <span className="kd-hop" aria-label="Loading"><i /><i /><i /></span>
              ) : (
                "Load more"
              )}
            </button>
          </div>
        )}
      </>
    );
  }

  lookupTrx = async (e) => {
    e.preventDefault();
    const trxID = this.state.trxQuery.trim();
    if (!trxID) return;
    this.setState({ trxLoading: true, trxResult: null, trxError: "" });
    try {
      const result = await searchBkashTransaction(trxID);
      if (result.ok) this.setState({ trxResult: result.transaction });
      else this.setState({ trxError: result.message });
    } catch (err) {
      this.setState({ trxError: err.message });
    }
    this.setState({ trxLoading: false });
  };

  renderTrxLookup() {
    const { trxQuery, trxLoading, trxResult, trxError } = this.state;
    const row = (k, v) => (
      <div className="kd-rf-trx-row" key={k}>
        <dt>{k}</dt>
        <dd>{v || "—"}</dd>
      </div>
    );
    return (
      <section className="kd-rf-bkash" aria-labelledby="kd-rf-bkash-title">
        <span className="kd-rf-bkash-mark" aria-hidden="true">৳</span>
        <h3 id="kd-rf-bkash-title">bKash lookup</h3>
        <p>Check a bKash transaction before you refund it.</p>
        <form onSubmit={this.lookupTrx} className="kd-rf-trx-form">
          <label className="kd-rf-trx-input">
            <Search size={15} aria-hidden="true" />
            <input
              value={trxQuery}
              onChange={(e) => this.setState({ trxQuery: e.target.value })}
              placeholder="TrxID, e.g. DJK70PQKZ1"
              aria-label="bKash transaction ID"
            />
          </label>
          <button type="submit" className="kd-rf-btn kd-rf-btn--bkash" disabled={trxLoading}>
            {trxLoading ? (
              <span className="kd-hop" aria-label="Checking"><i /><i /><i /></span>
            ) : (
              "Check"
            )}
          </button>
        </form>
        {trxError && <p className="kd-rf-trx-error" role="alert">{trxError}</p>}
        {trxResult && (
          <dl className="kd-rf-trx-result">
            {row("Status", trxResult.transactionStatus)}
            {row("Amount", `৳${trxResult.amount}`)}
            {row("Payer", trxResult.customerMsisdn || trxResult.payerAccount)}
            {row("Completed", trxResult.completedTime)}
            {row("Type", trxResult.transactionType)}
            {row("Max refundable", trxResult.maxRefundableAmount && `৳${trxResult.maxRefundableAmount}`)}
          </dl>
        )}
      </section>
    );
  }

  render() {
    const { activeTab, pending, approved, rejected } = this.state;
    const counts = {
      pending:  pending.docs.length,
      approved: approved.docs.length,
      rejected: rejected.docs.length,
    };

    return (
      <div className="kd-rf-page">
        <ToastContainer />
        {this.renderModal()}

        <Breadcrumb title="Refund requests" parent="Orders" />
        <div className="container-fluid">
          <div className="kd-rf-layout">
            <section className="card kd-orders-card kd-rf-main">
              <div className="kd-ord-top">
                <div className="kd-ord-title">
                  <span className="kd-ord-title-tile kd-st--peach" aria-hidden="true">
                    <RotateCcw size={20} />
                  </span>
                  <div>
                    <h2>Refund requests</h2>
                    <svg className="kd-squiggle" viewBox="0 0 120 10" aria-hidden="true" focusable="false">
                      <path d="M2 6 Q12 1 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Status stickers double as tabs */}
              <div className="kd-rf-tabs" role="tablist" aria-label="Refund status">
                {TABS.map((tab, i) => {
                  const info = TAB_INFO[tab];
                  const isActive = activeTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`kd-stat-tag kd-rf-tab kd-st--${info.tone}` + (isActive ? " is-active" : "")}
                      style={{ "--i": i }}
                      onClick={() => this.switchTab(tab)}
                    >
                      <span className="kd-stat-label">
                        <info.Icon size={13} aria-hidden="true" />
                        {info.label}
                      </span>
                      <span className="kd-stat-value">
                        {this.state[tab].loading
                          ? "…"
                          : tab !== "pending" && !this.state[tab].loaded
                            ? "—"
                            : counts[tab]}
                        {tab !== "pending" && this.state[tab].hasMore ? "+" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="kd-rf-body" role="tabpanel">
                {this.renderList(activeTab)}
              </div>
            </section>

            <aside className="kd-rf-side">{this.renderTrxLookup()}</aside>
          </div>
        </div>
      </div>
    );
  }
}

export default RefundRequests;
